import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysCostCenter } from '../../costCenter';
import { queryKeysSite } from '../queryKeys';

import { createSitesAndCostCentersViaCSV } from './createSitesAndCostCentersViaCSV';
import { type CreateSitesAndCostCentersViaCSVMutationOptions } from './types';

/**
 * Custom hook for creating sites together with their cost centers from a CSV file.
 *
 * @param options - Additional options for the useMutation hook.
 * @returns The result of the useMutation hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Admin/operation/create_sites_and_cost_center_via_csv_admin_site_cc_csv_post
 */
export const useMutationCreateSitesAndCostCentersViaCSV = (
  options?: CreateSitesAndCostCentersViaCSVMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => createSitesAndCostCentersViaCSV(file),
    ...options,
    onSettled(data, error, variables, onMutateResult, context) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysSite.getAll({}),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeysCostCenter.getAll({}),
      });

      return options?.onSettled?.(
        data,
        error,
        variables,
        onMutateResult,
        context,
      );
    },
  });
};
